import { ActionIcon, Button } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import { NumberField } from 'app/modules/ifc-editor/NumberField';
import {
    createOpening,
    formatLength,
    getSlabIssues,
    getWallIssues,
    moveJunction,
    renameStorey,
    roundPoint,
    setWallLength,
    updateWall,
    wallHeight,
    wallLength
} from 'app/modules/ifc-editor/utils/wallGeometry';

import type {
    BuildingSpecification,
    EditorSelection,
    OpeningKind,
    PlanPoint,
    SlabKind
} from 'app/modules/ifc-editor/types/buildingSpecification';

interface WallInspectorProps {
    specification: BuildingSpecification;
    storey: string;
    selection: EditorSelection;
    onSelect: (selection: EditorSelection) => void;
    onApply: (update: (current: BuildingSpecification) => BuildingSpecification) => void;
}

const sectionClassName = 'space-y-2 border-b border-border-light p-3';
const headingClassName = 'text-xs font-semibold uppercase text-text-light';
const selectClassName =
    'mt-0.5 h-7 w-full rounded-sm border border-border-light bg-background-default px-1 text-sm text-text-default';
const openingKinds: OpeningKind[] = ['Door', 'Window', 'Opening'];
const slabKinds: SlabKind[] = ['Foundation', 'Floor'];

const Issues = ({ issues }: { issues: string[] }) =>
    issues.length === 0 ? null : (
        <ul className="list-disc rounded-sm bg-red-50 py-1 pl-5 pr-2 text-xs text-red-700" role="alert">
            {issues.map((issue) => (
                <li key={issue}>{issue}</li>
            ))}
        </ul>
    );

/** Overview of all dimensions when nothing is selected; precise fields for the selected wall or slab. */
export const WallInspector = ({ specification, storey, selection, onSelect, onApply }: WallInspectorProps) => {
    const { t } = useTranslation();

    if (selection?.type === 'wall' && specification.walls[selection.index]) {
        const index = selection.index;
        const wall = specification.walls[index];
        const update = (changes: Parameters<typeof updateWall>[2]) =>
            onApply((current) => updateWall(current, index, changes));
        const movePoint = (end: 'start' | 'end', point: PlanPoint) =>
            onApply((current) => moveJunction(current, wall.storey, current.walls[index][end], point));

        return (
            <div data-testid="wall-inspector">
                <div className={sectionClassName}>
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">{t('labelWallEditorWall', { id: wall.id })}</span>
                        <span className="flex gap-1">
                            <ActionIcon
                                aria-label={t('buttonWallEditorDeleteWall')}
                                className="action-icon-small bg-transparent hover:bg-background-gray! icon-sm"
                                onClick={() => {
                                    onSelect(null);
                                    onApply((current) => ({
                                        ...current,
                                        walls: current.walls.filter((_, wallIndex) => wallIndex !== index)
                                    }));
                                }}
                            >
                                delete
                            </ActionIcon>
                            <ActionIcon
                                aria-label={t('close')}
                                className="action-icon-small bg-transparent hover:bg-background-gray! icon-sm"
                                onClick={() => onSelect(null)}
                            >
                                close
                            </ActionIcon>
                        </span>
                    </div>
                    <Issues issues={getWallIssues(specification, wall)} />
                    <NumberField
                        label={t('labelWallLength')}
                        min={0.05}
                        testId="wall-length"
                        value={Math.round(wallLength(wall) * 1000) / 1000}
                        onCommit={(value) =>
                            value !== null && onApply((current) => setWallLength(current, index, value))
                        }
                    />
                    <div className="text-xs text-text-light">{t('infoWallLengthStretch')}</div>
                    <div className="grid grid-cols-2 gap-2">
                        <NumberField
                            label={t('labelWallThickness')}
                            min={0.05}
                            testId="wall-thickness"
                            value={wall.thickness}
                            onCommit={(value) => value !== null && update({ thickness: value })}
                        />
                        <NumberField
                            label={t('labelWallHeight')}
                            min={0.1}
                            placeholder={t('placeholderStoreyHeight', {
                                height: wallHeight(specification, { ...wall, height: null })
                            })}
                            value={wall.height}
                            allowEmpty
                            onCommit={(value) => update({ height: value })}
                        />
                        <NumberField
                            label={t('labelWallBaseOffset')}
                            value={wall.baseOffset}
                            onCommit={(value) => value !== null && update({ baseOffset: value })}
                        />
                        <label className="block text-xs text-text-light">
                            {t('labelStorey')}
                            <select
                                aria-label={t('labelStorey')}
                                className={selectClassName}
                                value={wall.storey}
                                onChange={(event) => update({ storey: event.target.value })}
                            >
                                {specification.storeys.map((candidate) => (
                                    <option key={candidate.name} value={candidate.name}>
                                        {candidate.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <NumberField
                            label={t('labelWallStartX')}
                            value={wall.start.x}
                            onCommit={(value) => value !== null && movePoint('start', { ...wall.start, x: value })}
                        />
                        <NumberField
                            label={t('labelWallStartY')}
                            value={wall.start.y}
                            onCommit={(value) => value !== null && movePoint('start', { ...wall.start, y: value })}
                        />
                        <NumberField
                            label={t('labelWallEndX')}
                            value={wall.end.x}
                            onCommit={(value) => value !== null && movePoint('end', { ...wall.end, x: value })}
                        />
                        <NumberField
                            label={t('labelWallEndY')}
                            value={wall.end.y}
                            onCommit={(value) => value !== null && movePoint('end', { ...wall.end, y: value })}
                        />
                    </div>
                </div>

                <div className={sectionClassName}>
                    <div className="flex items-center justify-between">
                        <span className={headingClassName}>
                            {t('labelWallOpenings', { count: wall.openings.length })}
                        </span>
                        <Button
                            leftSection={<Icon aria-hidden="true" icon="add" />}
                            size={sizes.button.small}
                            variant={variants.button.secondary}
                            onClick={() => update({ openings: [...wall.openings, createOpening(wall)] })}
                        >
                            {t('buttonWallEditorAddOpening')}
                        </Button>
                    </div>
                    {wall.openings.map((opening, openingIndex) => {
                        const updateOpening = (changes: Partial<typeof opening>) =>
                            update({
                                openings: wall.openings.map((candidate, candidateIndex) =>
                                    candidateIndex === openingIndex ? { ...candidate, ...changes } : candidate
                                )
                            });
                        return (
                            <div key={openingIndex} className="space-y-1 rounded-sm bg-background-gray-ultra-light p-2">
                                <div className="flex items-center gap-2">
                                    <select
                                        aria-label={t('labelOpeningKind')}
                                        className={selectClassName}
                                        value={opening.kind}
                                        onChange={(event) => updateOpening({ kind: event.target.value as OpeningKind })}
                                    >
                                        {openingKinds.map((kind) => (
                                            <option key={kind} value={kind}>
                                                {t(`labelOpeningKind${kind}`)}
                                            </option>
                                        ))}
                                    </select>
                                    <ActionIcon
                                        aria-label={t('buttonWallEditorRemoveOpening')}
                                        className="action-icon-small bg-transparent hover:bg-background-gray! icon-sm"
                                        onClick={() =>
                                            update({
                                                openings: wall.openings.filter(
                                                    (_, candidateIndex) => candidateIndex !== openingIndex
                                                )
                                            })
                                        }
                                    >
                                        close
                                    </ActionIcon>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <NumberField
                                        label={t('labelOpeningOffset')}
                                        min={0}
                                        value={opening.offset}
                                        onCommit={(value) => value !== null && updateOpening({ offset: value })}
                                    />
                                    <NumberField
                                        label={t('labelOpeningWidth')}
                                        min={0.01}
                                        value={opening.width}
                                        onCommit={(value) => value !== null && updateOpening({ width: value })}
                                    />
                                    <NumberField
                                        label={t('labelOpeningHeight')}
                                        min={0.01}
                                        value={opening.height}
                                        onCommit={(value) => value !== null && updateOpening({ height: value })}
                                    />
                                    <NumberField
                                        label={t('labelOpeningSill')}
                                        min={0}
                                        value={opening.sillHeight}
                                        onCommit={(value) => value !== null && updateOpening({ sillHeight: value })}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (selection?.type === 'slab' && specification.slabs[selection.index]) {
        const index = selection.index;
        const slab = specification.slabs[index];
        const updateSlab = (changes: Partial<typeof slab>) =>
            onApply((current) => ({
                ...current,
                slabs: current.slabs.map((candidate, slabIndex) =>
                    slabIndex === index ? { ...candidate, ...changes } : candidate
                )
            }));
        const updatePoint = (pointIndex: number, point: PlanPoint) =>
            updateSlab({
                outline: slab.outline.map((candidate, candidateIndex) =>
                    candidateIndex === pointIndex ? roundPoint(point) : candidate
                )
            });

        return (
            <div data-testid="slab-inspector">
                <div className={sectionClassName}>
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">{t(`labelSlabKind${slab.kind}`)}</span>
                        <span className="flex gap-1">
                            <ActionIcon
                                aria-label={t('buttonWallEditorDeleteSlab')}
                                className="action-icon-small bg-transparent hover:bg-background-gray! icon-sm"
                                onClick={() => {
                                    onSelect(null);
                                    onApply((current) => ({
                                        ...current,
                                        slabs: current.slabs.filter((_, slabIndex) => slabIndex !== index)
                                    }));
                                }}
                            >
                                delete
                            </ActionIcon>
                            <ActionIcon
                                aria-label={t('close')}
                                className="action-icon-small bg-transparent hover:bg-background-gray! icon-sm"
                                onClick={() => onSelect(null)}
                            >
                                close
                            </ActionIcon>
                        </span>
                    </div>
                    <Issues issues={getSlabIssues(slab)} />
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block text-xs text-text-light">
                            {t('labelSlabKind')}
                            <select
                                aria-label={t('labelSlabKind')}
                                className={selectClassName}
                                value={slab.kind}
                                onChange={(event) => updateSlab({ kind: event.target.value as SlabKind })}
                            >
                                {slabKinds.map((kind) => (
                                    <option key={kind} value={kind}>
                                        {t(`labelSlabKind${kind}`)}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <NumberField
                            label={t('labelSlabThickness')}
                            min={0.05}
                            value={slab.thickness}
                            onCommit={(value) => value !== null && updateSlab({ thickness: value })}
                        />
                    </div>
                </div>
                <div className={sectionClassName}>
                    <div className="flex items-center justify-between">
                        <span className={headingClassName}>
                            {t('labelSlabOutline', { count: slab.outline.length })}
                        </span>
                        <Button
                            leftSection={<Icon aria-hidden="true" icon="add" />}
                            size={sizes.button.small}
                            variant={variants.button.secondary}
                            onClick={() => {
                                const last = slab.outline[slab.outline.length - 1];
                                const first = slab.outline[0];
                                updateSlab({
                                    outline: [
                                        ...slab.outline,
                                        roundPoint({ x: (last.x + first.x) / 2, y: (last.y + first.y) / 2 })
                                    ]
                                });
                            }}
                        >
                            {t('buttonWallEditorAddPoint')}
                        </Button>
                    </div>
                    {slab.outline.map((point, pointIndex) => (
                        <div key={pointIndex} className="flex items-end gap-2">
                            <span className="w-5 pb-1.5 text-xs text-text-light">{pointIndex + 1}</span>
                            <NumberField
                                label="X"
                                value={point.x}
                                onCommit={(value) => value !== null && updatePoint(pointIndex, { ...point, x: value })}
                            />
                            <NumberField
                                label="Y"
                                value={point.y}
                                onCommit={(value) => value !== null && updatePoint(pointIndex, { ...point, y: value })}
                            />
                            <ActionIcon
                                aria-label={t('buttonWallEditorRemovePoint')}
                                className="action-icon-small mb-0.5 bg-transparent hover:bg-background-gray! icon-sm"
                                disabled={slab.outline.length <= 3}
                                onClick={() =>
                                    updateSlab({
                                        outline: slab.outline.filter(
                                            (_, candidateIndex) => candidateIndex !== pointIndex
                                        )
                                    })
                                }
                            >
                                close
                            </ActionIcon>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const walls = specification.walls
        .map((wall, index) => ({ wall, index }))
        .filter(({ wall }) => wall.storey === storey);
    const slabs = specification.slabs
        .map((slab, index) => ({ slab, index }))
        .filter(({ slab }) => slab.storey === storey);

    return (
        <div data-testid="building-overview">
            <div className={sectionClassName}>
                <span className={headingClassName}>{t('labelWallEditorStoreys')}</span>
                {specification.storeys.map((candidate) => (
                    <div key={candidate.name} className="grid grid-cols-3 gap-2">
                        <label className="block min-w-0 text-xs text-text-light">
                            {t('labelStoreyName')}
                            <input
                                aria-label={t('labelStoreyName')}
                                className="mt-0.5 h-7 w-full rounded-sm border border-border-light px-1.5 text-sm text-text-default"
                                defaultValue={candidate.name}
                                onBlur={(event) => {
                                    const name = event.target.value.trim();
                                    if (
                                        name &&
                                        name !== candidate.name &&
                                        !specification.storeys.some((other) => other.name === name)
                                    ) {
                                        onApply((current) => renameStorey(current, candidate.name, name));
                                    } else {
                                        event.target.value = candidate.name;
                                    }
                                }}
                            />
                        </label>
                        <NumberField
                            label={t('labelStoreyElevation')}
                            value={candidate.elevation}
                            onCommit={(value) =>
                                value !== null &&
                                onApply((current) => ({
                                    ...current,
                                    storeys: current.storeys.map((other) =>
                                        other.name === candidate.name ? { ...other, elevation: value } : other
                                    )
                                }))
                            }
                        />
                        <NumberField
                            label={t('labelStoreyHeight')}
                            min={0.1}
                            value={candidate.height}
                            onCommit={(value) =>
                                value !== null &&
                                onApply((current) => ({
                                    ...current,
                                    storeys: current.storeys.map((other) =>
                                        other.name === candidate.name ? { ...other, height: value } : other
                                    )
                                }))
                            }
                        />
                    </div>
                ))}
            </div>

            <div className={sectionClassName}>
                <span className={headingClassName}>{t('labelWallEditorWalls', { count: walls.length, storey })}</span>
                <table className="w-full text-xs" data-testid="wall-table">
                    <thead className="text-left text-text-light">
                        <tr>
                            <th className="font-normal">{t('labelWallId')}</th>
                            <th className="font-normal">{t('labelWallLength')}</th>
                            <th className="font-normal">{t('labelWallThickness')}</th>
                            <th className="font-normal">{t('labelWallHeight')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {walls.map(({ wall, index }) => {
                            const hasIssues = getWallIssues(specification, wall).length > 0;
                            return (
                                <tr
                                    key={`${wall.id}-${index}`}
                                    className={`cursor-pointer hover:bg-background-gray-ultra-light ${hasIssues ? 'text-red-700' : ''}`}
                                    data-testid={`wall-row-${wall.id}`}
                                    onClick={() => onSelect({ type: 'wall', index })}
                                >
                                    <td className="py-0.5">{wall.id}</td>
                                    <td>{formatLength(wallLength(wall))}</td>
                                    <td>{formatLength(wall.thickness)}</td>
                                    <td>{formatLength(wallHeight(specification, wall))}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {slabs.length > 0 && (
                <div className={sectionClassName}>
                    <span className={headingClassName}>{t('labelWallEditorSlabs', { count: slabs.length })}</span>
                    {slabs.map(({ slab, index }) => (
                        <button
                            key={index}
                            className="flex w-full justify-between rounded-sm px-1 py-0.5 text-left text-xs hover:bg-background-gray-ultra-light"
                            data-testid={`slab-row-${index}`}
                            type="button"
                            onClick={() => onSelect({ type: 'slab', index })}
                        >
                            <span>{t(`labelSlabKind${slab.kind}`)}</span>
                            <span className="text-text-light">
                                {t('infoSlabSummary', {
                                    points: slab.outline.length,
                                    thickness: formatLength(slab.thickness)
                                })}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
